import { useRef, useState, useCallback, useMemo, useEffect } from "react"
import PropTypes from "prop-types"
import { get, chain } from "lodash"
import classNames from "classnames"
import { CardHeader, Card, IconButton, Badge } from "@mui/material"
import { FilterList, OfflineBolt } from "@mui/icons-material"
import { DATE, FILTER_ON_GROUP, DROPDOWN_OPTION } from "helpers/shapes"
import { isFilterValid } from "helpers/filters"
import { formatLongDate } from "helpers/formatters/dates"
import FilterSaver from "modules/filter-saver"
import AddFilter from "components/add-filter"
import DateSelector from "components/date-picker"
import Tooltip from "components/material/tooltip"
import { hasInstrumentPortfolioUniverseFilter } from "./helpers"
import AddUniverse from "./components/add-universe"
import "./styles.scss"

export default function SecuritiesSearchParameters({
  chips,
  filterOns,
  portfoliosList,
  filter,
  setFilter,
  watchlistsList,
  etfsList,
  swapsList,
  indexesList,
  universeDisabled,
  startDate,
  endDate,
  selectedDate,
  unmodeledDates,
  actionsDisabled,
  className,
  elevation,
  heapAddFilterId,
  account,
  modelId,
  noDateSelection,
  allowFiltersToggle,
  name,
  context,
  currency,
  selectedDateAdjusted,
  addedFilters,
  anchorEl,
  filtersVisible,
  toggleFilters,
  instrumentPortfolio,
  addNotification,
  updateFilters,
  setDate,
}) {
  const filterButtonRef = useRef(null)
  const [otherFilters, setOtherFilters] = useState([])
  const [universeFilters, setUniverseFilters] = useState([])
  const [internalFiltersVisible, setInternalFiltersVisible] = useState(filtersVisible === undefined ? false : null)

  const toggleInternalFilters = useCallback(() => {
    setInternalFiltersVisible(prev => !prev)
  }, [])

  const modifyFilterForSmartPortfolio = useCallback((listElement) => {
    const { alias, name: instrumentName } = instrumentPortfolio
    const { filter, name: savedSearchName } = listElement

    if (hasInstrumentPortfolioUniverseFilter(filter, alias)) {
      return
    }

    const newFilter = {
      ...filter,
      universe: [
        { type: "PORTFOLIO", notIn: alias },
        ...get(filter, "universe", []),
      ],
    }

    addNotification("info", null, `Although the saved search "${savedSearchName}" has been selected, it has been temporarily modified to exclude securities from "${instrumentName}"`)
    return newFilter
  }, [instrumentPortfolio, addNotification])

  const toggleFunction = useMemo(() => {
    return toggleFilters || toggleInternalFilters
  }, [toggleFilters, toggleInternalFilters])

  const modifyFilter = useMemo(() => {
    return context === "smart-portfolio" ? modifyFilterForSmartPortfolio : undefined
  }, [context, modifyFilterForSmartPortfolio])

  const showFilters = useMemo(() => {
    return filtersVisible !== undefined ? filtersVisible : internalFiltersVisible
  }, [filtersVisible, internalFiltersVisible])

  useEffect(() => {
    if (universeDisabled) {
      return
    }
    const [newUniverseFilters, newOtherFilters] = (addedFilters || []).reduce((acc, filter) => {
      if (filter.type === "securities" || filter.type === "universe") {
        acc[0].push(filter)
      } else {
        acc[1].push(filter)
      }
      return acc
    }, [[], []])
    setUniverseFilters(newUniverseFilters)
    setOtherFilters(newOtherFilters)
  }, [addedFilters, universeDisabled])

  const handleUpdateUniverseFilters = useCallback((universeFilters) => {
    setUniverseFilters(universeFilters)
  }, [])

  const handleUpdateFilters = useCallback((filters) => {
    const validFilters = filters.filter(filter => isFilterValid(filter))
    const validUniverseFilters = universeFilters.filter(filter => {
      return filter.operator && filter.filterOn && filter.filterOn.value && filter.values.length > 0
    }).map(filter => {
      const newFilter = {
        filterOn: {
          value: filter.filterOn.value,
        },
        operator: filter.operator,
      }

      if (filter.filterOn.value === "securities") {
        newFilter.filterOn.type = "securities"
        newFilter.values = filter.values.map(security => {
          return chain(security)
            .pick(["ticker", "mic", "internalId", "indexId"])
            .omitBy(value => !value)
            .value()
        })
      } else {
        newFilter.filterOn.type = "universe"
        newFilter.values = [filter.values[0].value]
      }

      return newFilter
    })
    updateFilters([...validUniverseFilters, ...validFilters])
  }, [updateFilters, universeFilters])

  const handleSetDate = useCallback(({ selectedDate }) => {
    setDate(selectedDate)
  }, [setDate])

  const handleUndeleteableFilter = useCallback((filter) => {
    return context === "smart-portfolio" && filter.type === "universe" && filter.filterOn?.value === "portfolio" && filter.operator?.value === "notIn" && filter.values[0].value === instrumentPortfolio.alias
  }, [context, instrumentPortfolio])

  return <>
    <Card className={classNames("components_securities-search-parameters", className)} elevation={elevation}>
      {!actionsDisabled && <CardHeader
        className="card-header"
        disableTypography
        title={
          <>
            <div className="left-side">
              {selectedDateAdjusted && <Tooltip title="The selected date has been adjusted to reflect data availability">
                <OfflineBolt className="warning" />
              </Tooltip>}
              {name && <div className="name"><span>{name}</span> on</div>}
              {noDateSelection ? <div>{formatLongDate(selectedDate)}</div> : <DateSelector
                earliestDate={startDate}
                latestDate={endDate}
                selectedDate={selectedDate}
                exclusionMap={unmodeledDates}
                onChange={handleSetDate}
              />}
            </div>
            <div className="right-side">
              <FilterSaver chips={chips} modelId={modelId} filter={filter} setFilter={setFilter} modifyFilter={modifyFilter} noSpinner />
              {allowFiltersToggle && <Tooltip title="Filter Positions">
                <IconButton
                  ref={filterButtonRef}
                  onClick={toggleFunction}
                  className={classNames("filters-toggle", { active: showFilters })}
                  size="large"
                >
                  <Badge overlap="rectangular" color="secondary" invisible={showFilters || addedFilters?.length === 0} badgeContent={addedFilters?.length}>
                    <FilterList />
                  </Badge>
                </IconButton>
              </Tooltip>}
            </div>
          </>
        }
      />}
    </Card>
    {((!allowFiltersToggle) || (allowFiltersToggle && showFilters)) && <AddFilter
      filterOns={filterOns}
      heapAddFilterId={heapAddFilterId}
      currency={currency}
      toggleFilters={toggleFunction}
      filtersVisible={showFilters}
      updateFilters={handleUpdateFilters}
      anchorEl={anchorEl || filterButtonRef.current}
      existingFilters={universeDisabled ? addedFilters : otherFilters}
      additionalFilterComponent={!universeDisabled && <AddUniverse
        updateFilters={handleUpdateUniverseFilters}
        portfoliosList={portfoliosList}
        watchlistsList={watchlistsList}
        etfsList={etfsList}
        swapsList={swapsList}
        indexesList={indexesList}
        account={account}
        modelId={modelId}
        existingFilters={universeFilters}
        filterDisabled={handleUndeleteableFilter}
      />}
      forceShowAdditionalFilterComponent={!universeDisabled}
      isApplyButtonDisabledFunction={() => universeDisabled ? true : universeFilters.length < 1}
    />}
  </>
}

SecuritiesSearchParameters.propTypes = {
  name: PropTypes.string,
  filterOns: PropTypes.arrayOf(FILTER_ON_GROUP).isRequired,
  filter: PropTypes.object,
  setFilter: PropTypes.func.isRequired,
  portfoliosList: PropTypes.arrayOf(DROPDOWN_OPTION),
  watchlistsList: PropTypes.arrayOf(DROPDOWN_OPTION),
  etfsList: PropTypes.arrayOf(DROPDOWN_OPTION),
  swapsList: PropTypes.arrayOf(DROPDOWN_OPTION),
  indexesList: PropTypes.arrayOf(DROPDOWN_OPTION),
  unmodeledDates: PropTypes.objectOf(PropTypes.bool),
  endDate: DATE,
  startDate: DATE,
  selectedDate: DATE,
  setDate: PropTypes.func,
  chips: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  })).isRequired,
  modelId: PropTypes.string.isRequired,
  universeDisabled: PropTypes.bool,
  actionsDisabled: PropTypes.bool,
  className: PropTypes.string,
  heapAddFilterId: PropTypes.string,
  elevation: PropTypes.number,
  account: PropTypes.string.isRequired,
  noDateSelection: PropTypes.bool,
  allowFiltersToggle: PropTypes.bool,
  context: PropTypes.string,
  instrumentPortfolio: PropTypes.object,
  addNotification: PropTypes.func.isRequired,
  currency: PropTypes.string,
  selectedDateAdjusted: PropTypes.bool,
  updateFilters: PropTypes.func.isRequired,
  addedFilters: PropTypes.arrayOf(PropTypes.object).isRequired,
  anchorEl: PropTypes.object,
  toggleFilters: PropTypes.func,
  filtersVisible: PropTypes.bool,
}
